import { useState, useEffect } from "react";
import { z } from "zod";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import toast from "react-hot-toast";
import { contactSalesSupport } from "../../../../../../schemas/SpeakWithAccountExecutive";
import TextArea from "../../../../../custom-ui/new-ui/TextArea";
import ToastAlert from "../../../../../custom-ui/toast-alert/ToastAlert";
import { useAuth } from "../../../../../../providers/auth";
import { createNewEntryForAccountExecutiveLeads } from "../../../../../../data/accountExecutiveLeads";
import { getUser } from "../../../../../../providers/database";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faEnvelope,
  faPaperPlane,
  faShieldHalved,
} from "@fortawesome/free-solid-svg-icons";

type TSpeakWithAccountExecutiveModalProps = {
  onCancel: () => void;
  onDone: () => void;
  source: string;
  type: string;
};

const ContactSalesSupport = ({
  onCancel,
  onDone,
  source,
  type,
}: TSpeakWithAccountExecutiveModalProps) => {
  const { user } = useAuth();
  const [userData, setUserData] = useState(null);
  const employerId = user?.claims?.employerId;

  // @ts-ignore
  const [formSchema] = useState(contactSalesSupport());

  type FormValues = z.infer<ReturnType<typeof contactSalesSupport>>;

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      message: "",
    },
    reValidateMode: "onBlur",
  });

  const {
    handleSubmit,
    control,
    formState: { errors, isSubmitting },
  } = form;

  const onSubmit = async ({ ...values }: FormValues) => {
    const { message } = values;

    const fullName = userData?.name;
    const email = userData?.email;
    const phoneNumber = userData?.phone;

    const accountExecutiveLeadsData: any = {
      fullName,
      email,
      phoneNumber,
      message,
      employerId: employerId,
      createdAt: new Date(),
      source: source,
      type: type,
    };

    await createNewEntryForAccountExecutiveLeads(accountExecutiveLeadsData)
      .then(() => {
        toast.success("Your form has been submitted successfully");
        setTimeout(() => {
          onDone();
        }, 3000);
      })
      .catch(() => {
        toast.error("There was an error submitting the form");
      });
  };

  useEffect(() => {
    getUser(user.id).then((userDataObj) => {
      setUserData(userDataObj);
    });
  }, []);

  return (
    <>
      <form className="w-full flex flex-col" onSubmit={handleSubmit(onSubmit)}>
        <div className="flex items-center gap-3 px-16 sm:px-24 pt-14 sm:pt-16 pb-8 pr-20 sm:pr-24">
          <div className="w-12 h-12 shrink-0 rounded-full bg-[#EAF3F8] text-[#15415E] flex items-center justify-center">
            <FontAwesomeIcon icon={faEnvelope} className="w-5 h-5" />
          </div>
          <div className="min-w-0 flex-1">
            <h2 className="text-[18px] sm:text-[20px] font-bold text-[#15415E] leading-tight">
              Get In Touch
            </h2>
          </div>
        </div>

        <div className="h-px bg-[#E8EEF2] mx-16 sm:mx-24" />

        <div className="px-16 sm:px-24 py-8 sm:py-10">
          <Controller
            control={control}
            name="message"
            render={(field) => (
              <TextArea
                {...field}
                display="Message"
                placeholder="Please write here"
                labelClassName="text-[13px] text-[#15415E] font-semibold mb-2"
                readOnly={false}
                error={errors?.message?.message}
                maxLength={500}
                required={true}
                className="h-[128px] border border-[#D5E0E6] bg-white shadow-none rounded-[10px] text-[14px] text-[#15415E] placeholder:text-[#A5BDC5] focus:ring-2 focus:ring-inset focus:ring-[#15415E]/20"
              />
            )}
          />

          <div className="flex items-start gap-2.5 mt-5 rounded-[10px] bg-[#F0F6FA] px-3.5 py-3">
            <FontAwesomeIcon
              icon={faShieldHalved}
              className="w-3.5 h-3.5 text-[#15415E] mt-0.5 shrink-0"
            />
            <p className="text-[12px] leading-[16px] text-[#5E7A88]">
              Our team will review your message and get back to you as
              soon as possible.
            </p>
          </div>
        </div>

        <div className="h-px bg-[#E8EEF2] mx-16 sm:mx-24" />

        <div className="px-16 sm:px-24 py-8 sm:py-10 flex flex-row items-center justify-center">
          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full h-[44px] rounded-full bg-[#15415E] text-white text-[14px] font-semibold inline-flex items-center justify-center gap-2 hover:bg-[#1d5278] active:bg-[#123348] transition-all duration-200 disabled:bg-[#636b71] disabled:cursor-not-allowed"
          >
            {!isSubmitting && (
              <FontAwesomeIcon icon={faPaperPlane} className="w-3.5 h-3.5" />
            )}
            {isSubmitting ? "Submitting..." : "Submit"}
          </button>
        </div>
      </form>
      <ToastAlert />
    </>
  );
};

export default ContactSalesSupport;
